/**
 * Rimette insieme il parlato dai messaggi `INTERIM:` del server.
 *
 * IL PUNTO, e non e' piu' quello di prima: **ogni `INTERIM:` porta tutto il
 * detto finora.** Il server lo dichiara in testa a `trascrizione_openai.py`
 * («al browser: "INTERIM:<tutto il testo detto finora>"») e lo fa in un punto
 * solo, `InterimCumulativo.prossimo()`, che serve tutti e due i motori: quello
 * OpenAI e il freno Google (`audio_streaming.py:350`). Quindi qui non si
 * ricuce niente: si SOSTITUISCE quello che c'e' con quello che arriva.
 *
 * VERIFICATO DAL VIVO, non dedotto dal codice (07/09/2026, preprod
 * `studierai-preprod-server`): 43 secondi di risposta vera con pause, **177
 * messaggi `INTERIM:`, zero ripartenze**, ognuno che continua il precedente
 * dalla prima parola fino a 700 caratteri. E su una seconda registrazione
 * vera, 45 secondi: stesso esito su 501 caratteri.
 *
 * PERCHE' PRIMA SI ACCODAVA, E PERCHE' ERA SBAGLIATO. La versione precedente
 * teneva separati «segmenti chiusi» e «segmento in corso» per reggere anche un
 * server che mandasse pezzi staccati: quando il messaggio nuovo non continuava
 * quello vecchio, apriva un segmento nuovo e lasciava il vecchio a schermo. Il
 * riconoscimento passava da una chiave normalizzata che regge maiuscole,
 * accenti e punteggiatura — ma NON le cifre. E con un modello che manda la
 * risposta intera in ogni messaggio, il conto non torna piu':
 *
 *     detto  : "il concilio si tenne nel millecentoquindici" -> "...nel 1815."
 *     visto  : "il concilio si tenne nel millecentoquindici Il concilio si
 *               tenne nel 1815."
 *
 * Bastava UNA cifra in cinque minuti di parlato per vedere due volte l'intera
 * risposta. Adesso la cifra sostituisce, e a schermo resta la stessa stringa
 * che va al correttore.
 *
 * IL PREZZO, dichiarato. Questo file adesso **richiede** il contratto
 * cumulativo. Contro un server che mandasse segmenti che ripartono da zero
 * (`origin/main` al 07/09: `corrente = ""` al `completed`, nessun `cuci`) si
 * vedrebbe solo l'ultimo segmento, non tutta la risposta. I due rami vanno
 * promossi insieme: il registratore non indovina piu' quale server ha davanti,
 * e non deve — indovinare era il difetto.
 */

/** Spazi normalizzati, ma il testo resta quello vero: e' cio' che si legge. */
const ripulisci = (testo) => (testo || "").replace(/\s+/g, " ").trim();

/**
 * La chiave con cui si decide se un messaggio e' un TRONCAMENTO di quello che
 * c'e' gia' a schermo. Serve solo a quello: a non far accorciare il testo
 * quando arriva una versione piu' corta dello stesso parlato.
 *
 * Ignora maiuscole, accenti e punteggiatura perche' il modello riscrive la
 * stessa frase in forme diverse ("perche" -> "Perche'") e senza questo un
 * troncamento non si riconoscerebbe.
 */
const chiave = (testo) =>
  testo
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

export const trascrizioneIniziale = () => ({ testo: "" });

/** Quello che si mostra: l'ultimo messaggio arrivato, che porta tutto. */
export const testoVisibile = (stato) => stato.testo;

/**
 * @param {{testo: string}} stato
 * @param {string} pezzo  contenuto di un messaggio `INTERIM:`
 */
export const aggiungiInterim = (stato, pezzo) => {
  const arrivato = ripulisci(pezzo);
  if (!arrivato) return stato; // un INTERIM vuoto non cancella niente
  if (!stato.testo) return { testo: arrivato };

  // L'unica cosa che NON si fa e' accorciare: se quello che arriva e' un
  // troncamento STRETTO di quello che c'e', e' un messaggio arretrato e si
  // tiene il piu' lungo. Il server ha la stessa guardia (`len(testo) <
  // len(interim_inviato)`), questa e' la stessa regola vista da qui.
  //
  // «Stretto» conta: a chiave uguale i due testi sono la stessa frase scritta
  // in due modi ("perche il quadrato" e "Perche' il quadrato."), e li' vince
  // il nuovo, che e' la forma pulita del modello — la stessa che va al
  // correttore. Con un `startsWith` senza il confronto di lunghezza restava a
  // schermo la versione senza accenti ne' punteggiatura.
  const attuale = chiave(stato.testo);
  const nuovo = chiave(arrivato);
  if (nuovo.length < attuale.length && attuale.startsWith(nuovo)) return stato;

  // Tutto il resto sostituisce: e' la stessa risposta riscritta meglio.
  return { testo: arrivato };
};

export default aggiungiInterim;
